/**
 * 路由守卫。
 *
 * 注意：前端守卫只是体验（避免闪现不该看的界面），真正的安全边界在后端
 * （见 shared/permissions.ts 的注释：前端隐藏按钮，后端做门禁）。
 *
 * 关于系统管理员（见方案 2B）：
 *  - 家长/学生端的路由**不认** system_admin，管理员访问会被送回 /admin
 *  - 管理路由只认 system_admin，其它角色一律送回自己的首页
 *  - 管理员没绑动态口令时，只能看到绑定页（第一道防线）
 */
import type { ReactElement, ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type { AccountRole } from '@fht/shared';
import { homePathForRole, isSystemAdmin } from '@fht/shared';
import { useAuth } from '../lib/auth';
import { FullPageLoading } from '../components/ui/Feedback';
import { AdminTotpPage } from '../pages/admin/AdminTotpPage';

/** 需要登录才能访问。 */
export function RequireAuth({
  children,
  role,
}: {
  children: ReactNode;
  /** 限定角色；不传表示任意**家庭内**角色（家长或学生）。 */
  role?: AccountRole;
}): ReactElement {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return <FullPageLoading label="正在恢复登录状态…" />;

  if (!user) {
    // 记住原地址，登录后跳回去
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // 落点按角色决定：管理员不能被送回家长首页（那会造成"/"与"/"之间的重定向死循环）
  const home = homePathForRole(user.role);

  if (role && user.role !== role) {
    return <Navigate to={home} replace />;
  }

  // 家庭端路由不接受系统管理员：它没有家庭数据权限（见方案 2B.1）
  if (!role && isSystemAdmin(user.role)) {
    return <Navigate to="/admin" replace />;
  }

  return <>{children}</>;
}

/** 仅未登录可见（登录/注册页）；已登录则按角色送回各自首页。 */
export function RequireGuest({ children }: { children: ReactNode }): ReactElement {
  const { user, isLoading } = useAuth();
  if (isLoading) return <FullPageLoading label="正在恢复登录状态…" />;
  if (user) return <Navigate to={homePathForRole(user.role)} replace />;
  return <>{children}</>;
}

/**
 * 仅系统管理员可见（管理后台）。
 *
 * 三道判断，顺序有讲究：
 *   1. 未登录 → 管理登录页（不是家长登录页：那是公网入口，管理员入口不对外）
 *   2. 不是管理员 → 送回他自己的首页（家长/学生端导航里本来就没有管理入口）
 *   3. 是管理员但没绑动态口令 → **只**渲染绑定页，children 完全不可达
 */
export function RequireSystemAdmin({ children }: { children: ReactNode }): ReactElement {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return <FullPageLoading label="正在恢复登录状态…" />;

  if (!user) {
    return <Navigate to="/admin/login" replace state={{ from: location.pathname }} />;
  }

  if (!isSystemAdmin(user.role)) {
    return <Navigate to={homePathForRole(user.role)} replace />;
  }

  // totpEnabled 来自 /auth/me（后端读的是 totp_enabled_at）
  if (!user.totpEnabled) {
    return <AdminTotpPage />;
  }

  return <>{children}</>;
}
